import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { updateOrganizationSchema } from '@netdeck/contracts';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { Field } from '../../shared/ui/Field';
import { useUpdateOrganization } from './api';

/**
 * Переименование организации.
 *
 * Окном, а не полем в шапке: название организации меняют раз в жизни —
 * при заведении платформы оно приходит из установки («Демонстрационная
 * организация») и правится один раз под настоящее имя. Поле, стоящее
 * в шапке постоянно, всё остальное время только предлагало бы задеть
 * его случайно.
 *
 * Слаг здесь не показывается и не правится: он разошёлся по ссылкам
 * и по именам потоков в go2rtc, и объяснять в этом окне, почему одно
 * меняется, а второе нет, значит рассказывать про устройство платформы
 * тому, кто просто ошибся в букве названия.
 */
export function OrganizationDialog({
  name,
  onClose,
}: {
  name: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [value, setValue] = useState(name);
  const update = useUpdateOrganization();

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const parsed = updateOrganizationSchema.safeParse({ name: value });

  function submit(event: FormEvent): void {
    event.preventDefault();

    if (!parsed.success) {
      return;
    }

    update.mutate(parsed.data, { onSuccess: onClose });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:items-center sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label={t('organization.dialogTitle')}
        onSubmit={submit}
        className="flex w-full max-w-[420px] flex-col gap-3 rounded-t-lg border border-line bg-surface p-4 sm:rounded-lg"
      >
        <h3 className="text-lg font-semibold">{t('organization.dialogTitle')}</h3>

        <Field
          label={t('organization.nameLabel')}
          required
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          hint={t('organization.nameHint')}
        />

        {update.error ? (
          <span className="text-sm text-down-fg">{errorText(update.error)}</span>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            icon="check"
            type="submit"
            variant="primary"
            size="sm"
            disabled={update.isPending || !parsed.success || value.trim() === name}
          >
            {update.isPending ? t('organization.saving') : t('common.action.save')}
          </Button>

          <Button size="sm" onClick={onClose} disabled={update.isPending}>
            {t('common.action.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
